/**
 * @design-contract CUR-113 + CUR-206
 *
 * Layout tokens for the chat surface. Numeric values (used by runway
 * arithmetic and scrollPaddingTop) and Tailwind class strings (used by the
 * cluster wrapper and PMV chrome) for the chat display.
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - This file is the single source of truth for chat layout dimensions.
 *     Inline `px-[16px]`, `gap-y-5`, `pinnedUserMessageTopGap = 64`, etc.
 *     are FORBIDDEN inside `components/chat/**` and `layouts/Chat*.tsx` —
 *     import from here instead. An ESLint rule
 *     (`chat-streaming-contract/no-inline-tokens`, implemented by
 *     `eslint-rules/no-inline-streaming-scroll-tokens.cjs`) enforces this.
 *   - Do NOT modify these values to make a failing test pass elsewhere.
 *     Adapt the consuming code instead. Every value here has been tuned
 *     against the CUR-113 + CUR-206 regression suite; changing one
 *     silently shifts the chat UI for every user. Symptoms range from
 *     visible jitter (CUR-113) to stranded TTS icons between phases
 *     (CUR-206) to runaway runway height (the ResizeObserver / state
 *     dep loop documented in `useTurnRunway`).
 *   - Plug-in (isPlugin=true) and side-pane (useSidePanelLayout=true)
 *     surfaces use a tighter set of values; consumers pick via the
 *     `*ForSurface` helpers at the bottom of the file.
 */

/**
 * Vertical distance from the top of the visible scroll region to the top of
 * the latest user message's bubble while it is pinned during an active
 * streaming response. Also used as `scrollPaddingTop` on the scroll
 * container so `scrollIntoView({ block: 'start' })` lands the user message
 * with this much breathing room, NOT flush against the chrome.
 *
 * Tightened for plug-in mode because the plug-in panel is narrow / short
 * and a 64px gap pushes the response below the fold on first paint.
 */
export const PINNED_USER_MESSAGE_TOP_GAP_PX = 64
export const PINNED_USER_MESSAGE_TOP_GAP_PX_PLUGIN = 12

/**
 * Floor on the runway height (the empty space we reserve below the pinned
 * user message so the assistant response has room to grow without the
 * reader being yanked). Used when measured viewport-derived runway would
 * be smaller than this — typically very short viewports.
 *
 * Lower in plug-in mode because the plug-in panel itself is short.
 */
export const ACTIVE_TURN_RUNWAY_MINIMUM_HEIGHT_PX = 320
export const ACTIVE_TURN_RUNWAY_MINIMUM_HEIGHT_PX_PLUGIN = 220

/**
 * Fraction of the visible viewport used as the runway target when there is
 * no better signal (no `latestTurn` rect or no scroll container yet).
 * Multiplied by `window.innerHeight` and clamped against the minimum above.
 */
export const ACTIVE_TURN_RUNWAY_VIEWPORT_RATIO = 0.8
export const ACTIVE_TURN_RUNWAY_VIEWPORT_RATIO_PLUGIN = 0.45

/**
 * Trailing gap left below the assistant cluster, between the bottom of the
 * cluster and the top of the composer. Tightened on plug-in for the same
 * vertical-budget reason as the rest of these.
 *
 * INVARIANT (FRAGILE): this value is baked into the runway math
 * (`measuredScrollBottom - turnTopAfterPinnedScroll - messageStackTrailingGap`).
 * If you change it, re-read the bottom-spacer composition comments in
 * `useTurnRunway.ts` — the rule "no standalone `+ messageStackTrailingGap`
 * in the spacer" depends on the trailing gap already being subtracted
 * inside `measuredTurnRunwayHeight`.
 */
export const MESSAGE_STACK_TRAILING_GAP_PX = 20
export const MESSAGE_STACK_TRAILING_GAP_PX_PLUGIN = 12

/**
 * Default height reserved at the bottom of the scroll content when we do
 * NOT have an actively streaming turn (and therefore no measured composer
 * height yet). Used as a fallback for the very first frame after mount,
 * before the ResizeObserver on `composerPanelRef` has reported a height.
 */
export const COMPOSER_SCROLL_SPACER_DEFAULT_HEIGHT_PX = 168
export const COMPOSER_SCROLL_SPACER_DEFAULT_HEIGHT_PX_PLUGIN = 120

/**
 * Distance from the absolute bottom of the scroll container that we still
 * consider "at the bottom". Used by both the onScroll handler that flips
 * `userHasScrolledUpRef` and by the follow-along RAF pin in
 * `useTurnScrollPin` — the two MUST agree or the pin will keep firing
 * while the scroll handler thinks the user has escaped (or vice versa).
 *
 * INVARIANT (FRAGILE): widening this threshold reintroduces the
 * "reader gets yanked to the bottom mid-stream" bug. Guarded by:
 * `does not fight a wheel-up while the user is actively scrolling`.
 */
export const BOTTOM_PROXIMITY_THRESHOLD_PX = 150

/**
 * Tailwind gap class applied to the cluster wrapper (the flex column that
 * holds the user message + assistant slots for the latest turn). Tight
 * on plug-in / side-pane, looser on the standard desktop chat surface.
 */
export const LATEST_TURN_GAP_CLASS_DESKTOP = 'gap-y-5'
export const LATEST_TURN_GAP_CLASS_COMPACT = 'gap-y-3'

/**
 * Tailwind classes applied to the assistant TEXT cluster wrapper (the
 * cluster that holds `assistantTextContainerRef` plus the feedback row).
 * `gap-y-7` is the vertical rhythm between the text bubble and the
 * feedback controls below it. The text bubble is intentionally NOT
 * inset horizontally on desktop — the slash-commands PR removed the
 * old `px-[16px]` here so the wrapper sits flush against the
 * message-stack gutter. The cluster-end feedback row in the PMV branch
 * still applies `ASSISTANT_CLUSTER_END_INSET_CLASS` (px-[16px]) so its
 * controls land at the same column as the PMV chrome's internal padding.
 */
export const ASSISTANT_TEXT_CLUSTER_CLASS = 'gap-y-7'
export const ASSISTANT_TEXT_CLUSTER_CLASS_COMPACT = 'gap-y-3'
export const ASSISTANT_TEXT_CLUSTER_CLASS_SIDEPANE = 'gap-y-3'

/**
 * Inset for the cluster-end feedback row when the cluster's last entry is
 * a PMV (rather than a text message). See the matching inset above for
 * the alignment invariant.
 */
export const ASSISTANT_CLUSTER_END_INSET_CLASS = 'px-[16px]'

/**
 * Outer width / padding for the message-stack scroll content. Three
 * variants:
 *   - `MESSAGE_STACK_OUTER_PADDING_CLASS_DESKTOP`: standard desktop
 *     surface (`w-full max-w-3xl`). The slash-commands PR widened the
 *     readable column from `max-w-[720px]` with `px-[60px]` padding to
 *     `max-w-3xl` (768px) flush — designers refer to this width in
 *     mocks. If you want to tighten the column, do it via a new token
 *     here, NOT inline.
 *   - `MESSAGE_STACK_OUTER_PADDING_CLASS_PLUGIN`: plug-in surface
 *     (`px-3 max-w-full`).
 *
 * Note: the slash-commands PR collapsed the previous side-pane vs.
 * desktop split — every non-plug-in surface (including landing-column
 * layouts: split-pane, scratch panel, editable canvas, minimized-task
 * panel) now reads the desktop variant.
 */
export const MESSAGE_STACK_OUTER_PADDING_CLASS_DESKTOP = 'w-full max-w-3xl'
export const MESSAGE_STACK_OUTER_PADDING_CLASS_PLUGIN = 'px-3 max-w-full'

/**
 * Inset for the PMV chrome itself (`pl-[16px]` thinking / `px-[16px]`
 * non-thinking title row, and `px-[16px]` for the expanded body / leave-
 * open hint). Exported so other consumers (e.g. ChatMessage's PMV-end
 * feedback render) can read it directly rather than hardcoding `16px`.
 *
 * Numeric value mirrors the Tailwind class string above (Tailwind compiles
 * `px-[16px]` to `padding-inline: 16px`). Kept separate so layout math
 * (e.g. width budgets) can use the number.
 */
export const PMV_CHROME_INSET_CLASS = 'px-[16px]'
export const PMV_CHROME_INSET_PX = 16

// ────────────────────────────────────────────────────────────────────────
// Per-surface helpers
// ────────────────────────────────────────────────────────────────────────

export interface ChatSurfaceFlags {
  isPlugin: boolean
  /**
   * "Landing-column" layouts (split-pane, scratch panel, editable canvas,
   * reserved minimized-task-panel slot) use the same tighter rhythm as
   * plug-in mode for the latest-turn gap. Wired from ChatDisplay's
   * `useLandingColumnLayout` flag.
   */
  useLandingColumnLayout?: boolean
}

export function pinnedUserMessageTopGapPx(flags: ChatSurfaceFlags): number {
  return flags.isPlugin ? PINNED_USER_MESSAGE_TOP_GAP_PX_PLUGIN : PINNED_USER_MESSAGE_TOP_GAP_PX
}

export function activeTurnRunwayMinimumHeightPx(flags: ChatSurfaceFlags): number {
  return flags.isPlugin
    ? ACTIVE_TURN_RUNWAY_MINIMUM_HEIGHT_PX_PLUGIN
    : ACTIVE_TURN_RUNWAY_MINIMUM_HEIGHT_PX
}

export function activeTurnRunwayViewportRatio(flags: ChatSurfaceFlags): number {
  return flags.isPlugin
    ? ACTIVE_TURN_RUNWAY_VIEWPORT_RATIO_PLUGIN
    : ACTIVE_TURN_RUNWAY_VIEWPORT_RATIO
}

export function messageStackTrailingGapPx(flags: ChatSurfaceFlags): number {
  return flags.isPlugin ? MESSAGE_STACK_TRAILING_GAP_PX_PLUGIN : MESSAGE_STACK_TRAILING_GAP_PX
}

export function composerScrollSpacerDefaultHeightPx(flags: ChatSurfaceFlags): number {
  return flags.isPlugin
    ? COMPOSER_SCROLL_SPACER_DEFAULT_HEIGHT_PX_PLUGIN
    : COMPOSER_SCROLL_SPACER_DEFAULT_HEIGHT_PX
}

export function latestTurnGapClass(flags: ChatSurfaceFlags): string {
  return flags.isPlugin || flags.useLandingColumnLayout
    ? LATEST_TURN_GAP_CLASS_COMPACT
    : LATEST_TURN_GAP_CLASS_DESKTOP
}

export interface AssistantTextSurfaceFlags {
  isCompact: boolean
  isSplitPaneLayout: boolean
}

export function assistantTextClusterClass(flags: AssistantTextSurfaceFlags): string {
  if (flags.isCompact) return ASSISTANT_TEXT_CLUSTER_CLASS_COMPACT
  if (flags.isSplitPaneLayout) return ASSISTANT_TEXT_CLUSTER_CLASS_SIDEPANE
  return ASSISTANT_TEXT_CLUSTER_CLASS
}

/**
 * Whether the PMV-branch cluster-end feedback row should apply the
 * `px-[16px]` inset. Matches the text-branch cluster wrapper's conditional.
 */
export function shouldApplyClusterEndInset(flags: AssistantTextSurfaceFlags): boolean {
  return !flags.isCompact && !flags.isSplitPaneLayout
}
